import axios from 'axios';
const _get = require('lodash.get');

import store from '../../../../../index';
import { TimeHelper } from '../../../../../../mixins/time';

const { convertDateStringToISO, getTimeZoneOffsetSecondsPayload } = TimeHelper.methods;
const parseError = error => {
  // https://github.com/axios/axios#handling-errors
  return _get(error, 'response.data');
};

export default {
  /*
   * get all games of provider
   */
  getProviderGames() {
    const url = `${store.state.app.config.baseUrl}/api/provider/game/get`;

    return axios.get(url, {
      params: {},
    }).then(({ data }) => {
      return data;
    }).catch((err) => {
      throw parseError(err);
    });
  },
  /*
   * get incoming order of provider
   */
  getIncomingOrders() {
    const url = `${store.state.app.config.baseUrl}/api/provider/next-orders`;
    return axios.get(url, {
      params: {},
    }).then(({ data }) => {
      let incomingOrders = null;
      if (data.nextOrders) {
        incomingOrders = data.nextOrders.filter(
          (order) => order.status === 1
        );
      }
      return incomingOrders;
    }).catch((err) => {
      throw parseError(err);
    });
  },
  /*
   * get my lobby sessions
   */
  getMyLobbySessions(isProvider) {
    const url = `${store.state.app.config.baseUrl}/api/provider-lobby-sessions/get-my-sessions`;
    return axios.get(url, {
      params: { is_provider: isProvider },
    }).then(({ data }) => {
      return data;
    }).catch((err) => {
      throw parseError(err);
    });
  },
  /*
   * get old lobby sessions
   */
  getOldLobbySessions(isProvider, page = 1, limit = 3) {
    const url = `${store.state.app.config.baseUrl}/api/provider-lobby-sessions/get-old-sessions`;
    return axios.get(url, {
      params: { is_provider: isProvider, page, limit },
    }).then(({ data }) => {
      return data;
    }).catch((err) => {
      throw parseError(err);
    });
  },
  /*
   * retrieves bulk public information for a provider including future announcements that are not visible to the public
   */
  getProviderInfoForEdit() {
    const url = `${store.state.app.config.baseUrl}/api/provider/editable`;

    return axios.get(url, {
      params: getTimeZoneOffsetSecondsPayload(),
    }).then(({ data }) => {
      return data;
    }).catch((err) => {
      throw parseError(err);
    });
  },
  /*
   * updates an existing provider public profile
   */
  updatePublicProfile(profile) {
    const url = `${store.state.app.config.baseUrl}/api/provider/public-profile/update`;

    const formData = new FormData();
    formData.append('region', +profile.region);
    formData.append('languages', JSON.stringify(profile.languages));
    formData.append('description', profile.description);
    formData.append('updateImage', 0);

    if (profile.updateImage) {
      formData.append('imageFile', profile.imageFile);
      formData.set('updateImage', 1);
    }

    return axios.post(url, formData)
      .then(({ data }) => data)
      .catch((err) => {
        throw parseError(err);
      });
  },
  /*
   * updates a provider weekly availability, potentially cancelling conflicting orders
   */
  updateAvailability(availability, conflictingOrdersAction) {
    const url = `${store.state.app.config.baseUrl}/api/provider/weekly-availability/update`;

    return axios.post(url, {
      availability: JSON.stringify(availability),
      conflictingOrdersAction,
      ...getTimeZoneOffsetSecondsPayload(),
    })
      .then(({ data }) => data)
      .catch((err) => {
        throw parseError(err);
      });
  },
  addOrUpdateService(service) {
    let url = `${store.state.app.config.baseUrl}/api/provider/service/add`;
    if (service.id) {
      url = `${store.state.app.config.baseUrl}/api/provider/service/update`;
    }

    return axios.post(url, {
      ...service,
      'price': +service.price,
      'requirements': JSON.stringify(service.requirements),
    })
      .then(({ data }) => data)
      .catch((err) => {
        throw parseError(err);
      });
  },
  removeService(service, conflictingOrdersAction) {
    const url = `${store.state.app.config.baseUrl}/api/provider/service/remove`;

    return axios.post(url, {
      id: service.id,
      conflictingOrdersAction,
    }).then(() => {
      return { removedId: service.id };
    }).catch(err => {
      throw parseError(err);
    });
  },
  addOrUpdateGame(game) {
    let url = `${store.state.app.config.baseUrl}/api/provider/game/add`;
    if (game.id) {
      url = `${store.state.app.config.baseUrl}/api/provider/game/update`;
    }

    const formData = new FormData();
    formData.append('name', game.name || '');
    formData.append('description', game.description || '');
    formData.append('gameCredentials', game.gameCredentials ? JSON.stringify(game.gameCredentials) : '');
    formData.append('updateImage', 0);
    if (game.id) {
      formData.append('id', game.id);
    }
    if (game.updateImage) {
      formData.append('imageFile', game.imageFile);
      formData.set('updateImage', 1);
    }

    return axios.post(url, formData)
      .then(({ data }) => data)
      .catch((err) => {
        throw parseError(err);
      });
  },
  removeGame(game, conflictingOrdersAction) {
    const url = `${store.state.app.config.baseUrl}/api/provider/game/remove`;

    return axios.post(url, {
      id: game.id,
      conflictingOrdersAction,
    }).then(() => {
      return { removedId: game.id };
    }).catch(err => {
      throw parseError(err);
    });
  },
  addOrUpdateAnnouncement(announcement) {
    let url = `${store.state.app.config.baseUrl}/api/provider/announcement/add`;
    if (announcement.id) {
      url = `${store.state.app.config.baseUrl}/api/provider/announcement/update`;
    }

    const formData = new FormData();
    formData.append('title', announcement.title);
    formData.append('description', announcement.description || '');
    formData.append('startDate', convertDateStringToISO(announcement.startDate));
    formData.append('endDate', convertDateStringToISO(announcement.endDate));
    formData.append('updateImage', 0);
    if (announcement.id) {
      formData.append('id', announcement.id);
    }
    if (announcement.updateImage) {
      formData.append('imageFile', announcement.imageFile);
      formData.set('updateImage', 1);
    }

    return axios.post(url, formData)
      .then(({ data }) => data)
      .catch((err) => {
        throw parseError(err);
      });
  },
  removeAnnouncement(announcement) {
    const url = `${store.state.app.config.baseUrl}/api/provider/announcement/remove`;

    return axios.post(url, {
      id: announcement.id,
    }).then(() => {
      return { removedId: announcement.id };
    }).catch((err) => {
      throw parseError(err);
    });
  },
  getListAllowedGames() {
    const url = `${store.state.app.config.baseUrl}/api/queueup-get-list-allowed-games`;

    return axios.get(url)
      .then(({ data }) => {
        return data;
      }).catch((err) => {
        throw parseError(err);
      });
  },
};
